import React from 'react';
import { dateOptions, dateTimeOptions } from '../../utils/dates';
import PSIGPMChart from './PSIGPMChart';
import FireFlowFlowHydrantList from './FireFlowFlowHydrantList';

const FireFlowReport = ({ fireFlowReport, loading }) => {
  const testDate = new Date(fireFlowReport?.testDate ?? null);
  const testDateString = new Intl.DateTimeFormat('en-US', dateTimeOptions).format(testDate);

  const testSuccess = fireFlowReport?.percentDrop >= 0.25;

  const seconds = fireFlowReport?.elapsedSeconds ?? 0;
  const elapsedMinutes = `${Math.floor(seconds / 60)}:${seconds % 60}`
  
  return (
    <div className="hydrant-test force-print-color">
      {loading && <div className="spinner"></div>}
      {!loading && !fireFlowReport && (
        <div className="empty-body">
          <div className="empty-text">No Test Found</div>
          <div className="empty-text-small">Unable to find a fire flow test.</div>
        </div>
      )}
      {!loading && fireFlowReport && (
        <>
          <div className="fireflow-header bottom-barred">
            <div className="company-info">
              <div className="company-detail">
                <label>Company Name:</label>
                <span>{fireFlowReport?.companyName}</span>
              </div>
            </div>
            <div className="fireflow-info">
              <div className="fireflow-date">
                <label>Test Date:</label>
                <span>{testDateString}</span>
              </div>
              <div className="fireflow-classification">
                <div className="fireflow-classification-title">
                  <span>NFPA Classification</span>
                </div>
                <div className={`fireflow-classification-code ${fireFlowReport?.nfpaClassificationColor?.toLowerCase()}`}>
                  <span>{fireFlowReport?.nfpaClassificationColor}</span>
                  <span>{fireFlowReport?.nfpaClassificationRating}</span>
                </div>
                <div className="fireflow-classification-number">
                  <span>{fireFlowReport?.nfpaClassificationValue?.toFixed(2)}</span>
                </div>
              </div>
              <div className="fireflow-conclusion">
                {testSuccess ? <span className="success">Test did reach recommended drop of 25% per NFPA 291</span>
                : <span className="fail">Test did not reach recommended drop of 25% per NFPA 291</span>}
              </div>
            </div>
          </div>
          <div className="location border-none">
            <div className="location-info">
              <div className="location-detail">
                <label>Test&nbsp;Hydrant:</label>
                <span>{fireFlowReport?.testHydrant}</span>
              </div>
              <div className="location-detail">
                <label>Address:</label>
                <span>{fireFlowReport?.address}</span>
              </div>
              <div className="location-detail">
                <label>Cross Street:</label>
                <span>{fireFlowReport?.crossStreet}</span>
              </div>
              <div className="location-detail">
                <label>Location:</label>
                <span>{fireFlowReport?.location}</span>
              </div>
              <div className="location-detail">
                <label>District:</label>
                <span>{fireFlowReport?.district}</span>
              </div>
              <div className="location-detail">
                <label>Sub-Division:</label>
                <span>{fireFlowReport?.subDivision}</span>
              </div>
            </div>
            <div className="gps-info">
              <div className="gps-detail">
                <label>Latitude:</label>
                <span>{fireFlowReport?.latitude?.toFixed(6)}</span>
              </div>
              <div className="gps-detail">
                <label>Longitude:</label>
                <span>{fireFlowReport?.longitude?.toFixed(6)}</span>
              </div>
              <div className="gps-detail">
                <label>Elevation:</label>
                <span>{fireFlowReport?.elevation?.toFixed(2)}</span>
              </div>
              <div className="gps-detail">
                <label>State X / Y:</label>
                <span>{fireFlowReport?.stateX} / {fireFlowReport?.stateY}</span>
              </div>
            </div>
          </div>
          <div className="hydrant-info v-barred">
            <div className="hydrant-info-detail">
              <label>Pumpers:</label>
              <span>{fireFlowReport?.pumpers}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Nozzles:</label>
              <span>{fireFlowReport?.nozzles}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Open Direction:</label>
              <span>{fireFlowReport?.openDirection}</span>
            </div>
          </div>
          <div className="hydrant-info">
            <div className="hydrant-info-detail">
              <label>Manufacturer:</label>
              <span>{fireFlowReport?.manufacturer}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Installed:</label>
              <span>{fireFlowReport?.installedDate ? new Date(fireFlowReport.installedDate).toLocaleDateString('en-US', dateOptions) : ''}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Vandal Proof:</label>
              <span>{fireFlowReport?.vandalProof}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Model:</label>
              <span>{fireFlowReport?.model}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Main Size:</label>
              <span>{fireFlowReport?.mainSize?.toFixed(2)}</span>
            </div>
            <div className="hydrant-info-detail">
              <label>Bury Depth:</label>
              <span>{fireFlowReport?.buryDepth?.toFixed(2)}</span>
            </div>
          </div>
          
          <FireFlowFlowHydrantList flowHydrants={fireFlowReport?.flowHydrants} />
          
          <div className="flow-test-result-details">
            <div className="result-detail">
              <label>Pitot / Nozzle PSI:</label>
              <span>{fireFlowReport?.nozzlePSI}</span>
            </div>
            <div className="result-detail">
              <label>Total Gallons Used:</label>
              <span>{fireFlowReport?.totalGallonsUsed}</span>
            </div>
            <div className="result-detail">
              <label>Static PSI:</label>
              <span>{fireFlowReport?.staticPSI}</span>
            </div>
            <div className="result-detail">
              <label>Max GPM during test:</label>
              <span>{fireFlowReport?.maxGPM}</span>
            </div>
            <div className="result-detail">
              <label>Residual PSI:</label>
              <span>{fireFlowReport?.residualPSI}</span>
            </div>
            <div className="result-detail">
              <label>Elapsed Time Min:Sec:</label>
              <span>{elapsedMinutes}</span>
            </div>
            <div className="result-detail">
              <label>Percent Drop:</label>
              <span>{(fireFlowReport?.percentDrop * 100).toFixed(2)}%</span>
            </div>
            <div className="result-detail">
              <label>Predicted GPM @ 20 PSI:</label>
              <span>{fireFlowReport?.predictedGPMAt20PSI.toFixed(2)}</span>
            </div>
          </div>

          <PSIGPMChart psiGPMs={fireFlowReport?.psiGPMs} />
        </>
      )}
    </div>
  );
};

export default FireFlowReport;